import { useCallback, useMemo } from 'react';
import { rage, useObservable } from '~/rage';
import {
	isRgbPaintType,
	IVehicleBodyColor,
	IVehiclePaint,
	ModalButtonColor,
	ModalType,
	rgbToHex,
	VehiclePaintType,
	VehicleTuningCategory,
	VehicleWheelTireProfile,
	WalletType,
} from '~/shared';
import {
	CategoryTypes,
	colorSelectItems,
} from '../components/ColorSelect/ColorSelect.model';
import {
	getWheelSettingsTotalPrice,
	isDefaultValues,
} from '../components/WheelSettings/WheelSettings.model';
import { TuningStudioStage, useLayoutSelector } from '../redux';
import { getEffectiveTuningSelection } from '../utils/resolveTuningShopVariant';
import { useTuningCategories } from './useTuningCategories';
import { useVehiclePaintLoader } from './useVehiclePaintLoader';

const useBuy = () => {
	const paintGroups = useObservable(
		rage.vehiclePaintShop.getVehiclePaintTypes(),
	);
	const windowTints = useObservable(
		rage.vehiclePaintShop.getVehicleWindowTints(),
	);
	const rimsColorsPrice = useObservable(
		rage.vehiclePaintShop.getVehicleRimsColorPrice(),
	);
	const rimsColors = useObservable(
		rage.vehiclePaintShop.getVehicleRimsColors(),
	);
	const { shopItems, nonExteriorCategories, ownedWheelSettings } =
		useTuningCategories();
	const wheelsSetupPrice = useObservable(
		rage.vehicleTuningShop.getWheelsSetupPrice(),
	);
	const wheelTireProfile = useObservable(
		rage.vehicleTuningShop.getWheelTireProfile(),
	);
	const tireProfileChangePrice = useObservable(
		rage.vehicleTuningShop.getTireProfileChangePrice(),
	);
	const modalState = useObservable(rage.modal.getCurrentModal());
	const activeStage = useLayoutSelector(
		(state) => state.tuningStudio.activeStage,
	);
	const activeIndex = useLayoutSelector(
		(state) => state.tuningStudio.activeIndex,
	);
	const activeTuningCategory = useLayoutSelector(
		(state) => state.tuningStudio.activeTuningCategory,
	);
	const activeTuningSubcategory = useLayoutSelector(
		(state) => state.tuningStudio.activeTuningSubcategory,
	);
	const wheelSettings = useLayoutSelector(
		(state) => state.tuningStudio.wheelSettings,
	);
	const colorPickerSelectedColor = useLayoutSelector(
		(state) => state.colorPicker.selectedColor,
	);
	const activePaintGroup = useLayoutSelector(
		(state) => state.colorPicker.paintGroup,
	);
	const ownedVehiclePaint = useLayoutSelector(
		(state) => state.carPaint.ownedVehiclePaint,
	);
	const pearlColor = useLayoutSelector((state) => state.colorPicker.pearlColor);
	const indexedColor = useLayoutSelector(
		(state) => state.colorPicker.indexedColor,
	);
	const rimsColor = useLayoutSelector((state) => state.colorPicker.rimsColor);
	const brakesColor = useLayoutSelector(
		(state) => state.colorPicker.brakesColor,
	);
	const interfaceHidden = useLayoutSelector(
		(state) => state.tuningStudio.interfaceHidden,
	);
	const activeVariants = useLayoutSelector(
		(state) => state.tuningStudio.activeVariants,
	);
	const selectedTireProfile = useLayoutSelector(
		(state) => state.tuningStudio.tireProfile,
	);

	const brakesColors = useObservable(
		rage.vehiclePaintShop.getVehicleBrakesColors(),
	);

	const { loadVehiclePaint } = useVehiclePaintLoader();

	const filteredShopItems = useMemo(() => {
		if (activeTuningSubcategory) {
			return shopItems.filter(
				(item) => item.subcategoryId === activeTuningSubcategory,
			);
		}
		if (activeTuningCategory) {
			return shopItems.filter(
				(item) => item.categoryId === activeTuningCategory,
			);
		}
		return [];
	}, [shopItems, activeTuningCategory, activeTuningSubcategory]);

	const activeShopItem = useMemo(() => {
		return filteredShopItems[activeIndex];
	}, [filteredShopItems, activeIndex]);

	const currentItem = colorSelectItems[activeIndex];
	const isPaintingWorks = currentItem?.type === CategoryTypes.PaintingWorks;
	const isPaintingInterior =
		currentItem?.type === CategoryTypes.PaintingInterior;
	const isPaintingWheels = currentItem?.type === CategoryTypes.PaintingWheels;
	const isBrakes = currentItem?.type === CategoryTypes.Brakes;

	const handleBuy = useCallback(
		(walletType: WalletType) => {
			if (modalState || interfaceHidden) {
				return;
			}

			if (activeStage === TuningStudioStage.MAIN) {
				const isWheelSettingsSelected =
					activeIndex > 2 &&
					nonExteriorCategories[activeIndex - 3]?.id ===
						VehicleTuningCategory.WHEEL_SETTINGS;

				const currentTireProfile =
					wheelTireProfile ?? VehicleWheelTireProfile.MEDIUM;
				const hasWheelSettingsChanged =
					ownedWheelSettings &&
					!isDefaultValues(wheelSettings, ownedWheelSettings);
				const hasTireProfileChanged =
					selectedTireProfile !== currentTireProfile;
				const hasChanges = hasWheelSettingsChanged || hasTireProfileChanged;

				if (isWheelSettingsSelected && hasChanges) {
					let totalPrice = 0;
					if (hasWheelSettingsChanged && ownedWheelSettings) {
						totalPrice += getWheelSettingsTotalPrice(
							wheelSettings,
							wheelsSetupPrice,
							ownedWheelSettings,
						);
					}
					if (hasTireProfileChanged) {
						totalPrice += tireProfileChangePrice ?? 0;
					}

					const text = `Вы действительно хотите оплатить ${
						walletType === WalletType.Cash ? 'наличными' : 'картой'
					} настройку колёс`;

					rage.modal.showModal(
						{
							type: ModalType.Price,
							title: 'Подтверждение',
							text: text,
							price: totalPrice,
							wallet: walletType,
							acceptButton: {
								text: 'Оплатить',
								color: ModalButtonColor.Success,
							},
							cancelButton: {
								text: 'Отменить',
								color: ModalButtonColor.Secondary,
							},
						},
						(result) => {
							if (result.isAccepted) {
								const selectedWallet =
									(result as { value?: WalletType }).value ?? walletType;
								const tuning = {
									...(hasWheelSettingsChanged ? { wheels: wheelSettings } : {}),
									...(hasTireProfileChanged
										? { tiresProfile: selectedTireProfile }
										: {}),
								};
								rage.vehicleTuningShop.buy(tuning, selectedWallet);
							}
						},
					);
				}
			}

			// Тюнинг
			if (activeStage === TuningStudioStage.TUNING_MENU) {
				if (!activeShopItem) {
					return;
				}

				const effective = getEffectiveTuningSelection(
					activeShopItem,
					activeVariants,
				);

				const text = `Вы действительно хотите оплатить ${
					walletType === WalletType.Cash ? 'наличными' : 'картой'
				} товар "${effective.installLabel}"`;

				rage.modal.showModal(
					{
						type: ModalType.Price,
						title: 'Подтверждение',
						text: text,
						price: effective.price,
						wallet: walletType,
						acceptButton: { text: 'Оплатить', color: ModalButtonColor.Success },
						cancelButton: {
							text: 'Отменить',
							color: ModalButtonColor.Secondary,
						},
					},
					(result) => {
						if (result.isAccepted) {
							const selectedWallet =
								(result as { value?: WalletType }).value ?? walletType;
							if (activeShopItem.categoryId === VehicleTuningCategory.KITS) {
								rage.vehicleTuningShop.buy(
									{
										itemsKit: activeShopItem.id,
									},
									selectedWallet,
								);
							} else {
								rage.vehicleTuningShop.buy(
									{
										items: [effective.id],
									},
									selectedWallet,
								);
							}
						}
					},
				);
				return;
			}

			// Тонировка
			if (activeStage === TuningStudioStage.TINTING) {
				const item = windowTints[activeIndex];
				if (!item) {
					return;
				}

				const vehiclePaint: Partial<IVehiclePaint> = {
					windowTint: item.value,
				};
				const text = `Вы действительно хотите оплатить ${
					walletType === WalletType.Cash ? 'наличными' : 'картой'
				} товар "${item.name}"`;

				rage.modal.showModal(
					{
						type: ModalType.Price,
						title: 'Подтверждение',
						text: text,
						wallet: walletType,
						price: item.price,
						acceptButton: { text: 'Оплатить', color: ModalButtonColor.Success },
						cancelButton: {
							text: 'Отменить',
							color: ModalButtonColor.Secondary,
						},
					},
					(result) => {
						if (result.isAccepted) {
							rage.vehiclePaintShop.buy(vehiclePaint).then(() => {
								rage.notifications.showSuccess('Поздравляем с покупкой!');
								loadVehiclePaint();
							});
						}
					},
				);
				return;
			}

			// Покраска
			if (activeStage === TuningStudioStage.PAINT) {
				const vehiclePaint: Partial<IVehiclePaint> = {};

				if (isBrakes) {
					vehiclePaint.brakesColor = brakesColor;
					const selectedColorName = brakesColors.find(
						(c) => c.index === brakesColor,
					)?.name;
					const price = paintGroups.find(
						(item) => item.type === VehiclePaintType.Classic,
					)?.price;
					const text = `Вы действительно хотите оплатить ${
						walletType === WalletType.Cash ? 'наличными' : 'картой'
					} покраску суппортов "${selectedColorName ?? ''}"?`;

					rage.modal.showModal(
						{
							type: ModalType.Price,
							title: 'Подтверждение',
							text: text,
							wallet: walletType,
							price: price || 0,
							acceptButton: {
								text: 'Оплатить',
								color: ModalButtonColor.Success,
							},
							cancelButton: {
								text: 'Отменить',
								color: ModalButtonColor.Secondary,
							},
						},
						(result) => {
							if (result.isAccepted) {
								rage.vehiclePaintShop.buy(vehiclePaint).then(() => {
									rage.notifications.showSuccess('Поздравляем с покупкой!');
									loadVehiclePaint();
								});
							}
						},
					);
					return;
				}

				if (isPaintingWheels) {
					const selectedRimsColor = rimsColors.find(
						(item) => item.index === rimsColor,
					);
					vehiclePaint.rimsColor = selectedRimsColor?.index;
					const text = `Вы действительно хотите оплатить ${
						walletType === WalletType.Cash ? 'наличными' : 'картой'
					} покраску колёс "${selectedRimsColor?.name}"`;
					rage.modal.showModal(
						{
							type: ModalType.Price,
							title: 'Подтверждение',
							text: text,
							wallet: walletType,
							price: rimsColorsPrice,
							acceptButton: {
								text: 'Оплатить',
								color: ModalButtonColor.Success,
							},
							cancelButton: {
								text: 'Отменить',
								color: ModalButtonColor.Secondary,
							},
						},
						(result) => {
							if (result.isAccepted) {
								rage.vehiclePaintShop.buy(vehiclePaint).then(() => {
									rage.notifications.showSuccess('Поздравляем с покупкой!');
									loadVehiclePaint();
								});
							}
						},
					);
					return;
				}

				if (isPaintingInterior) {
					vehiclePaint.interiorColor = rgbToHex(colorPickerSelectedColor);
					const price = paintGroups.find(
						(item) => item.type === VehiclePaintType.Classic,
					)?.price;
					const text = `Вы действительно хотите оплатить ${
						walletType === WalletType.Cash ? 'наличными' : 'картой'
					} покраску салона?`;

					rage.modal.showModal(
						{
							type: ModalType.Price,
							title: 'Подтверждение',
							text: text,
							wallet: walletType,
							price: price || 0,
							acceptButton: {
								text: 'Оплатить',
								color: ModalButtonColor.Success,
							},
							cancelButton: {
								text: 'Отменить',
								color: ModalButtonColor.Secondary,
							},
						},
						(result) => {
							if (result.isAccepted) {
								rage.vehiclePaintShop.buy(vehiclePaint).then(() => {
									rage.notifications.showSuccess('Поздравляем с покупкой!');
									loadVehiclePaint();
								});
							}
						},
					);
					return;
				}

				if (isPaintingWorks) {
					const currentPaintGroup = activePaintGroup || paintGroups[0];
					if (!currentPaintGroup) return;

					const color = isRgbPaintType(currentPaintGroup.type)
						? rgbToHex(colorPickerSelectedColor)
						: indexedColor;
					const bodyColor: Partial<IVehicleBodyColor> = {
						color: color === 0 ? 0 : color || '000000',
						type: currentPaintGroup.type,
					};

					if (currentPaintGroup.type === VehiclePaintType.Pearl) {
						bodyColor.pearlColor = pearlColor;
					}

					vehiclePaint.bodyColor = bodyColor as IVehicleBodyColor;

					const price = currentPaintGroup.price;
					const text = `Вы действительно хотите оплатить ${
						walletType === WalletType.Cash ? 'наличными' : 'картой'
					} покраску "${currentPaintGroup.name}"?`;

					rage.modal.showModal(
						{
							type: ModalType.Price,
							title: 'Подтверждение',
							text: text,
							wallet: walletType,
							price: price,
							acceptButton: {
								text: 'Оплатить',
								color: ModalButtonColor.Success,
							},
							cancelButton: {
								text: 'Отменить',
								color: ModalButtonColor.Secondary,
							},
						},
						(result) => {
							if (result.isAccepted) {
								rage.vehiclePaintShop.buy(vehiclePaint).then(() => {
									rage.notifications.showSuccess('Поздравляем с покупкой!');
									loadVehiclePaint();
								});
							}
						},
					);
					return;
				}
			}
		},
		[
			modalState,
			interfaceHidden,
			activeStage,
			windowTints,
			activeShopItem,
			activeTuningCategory,
			activeTuningSubcategory,
			isBrakes,
			isPaintingWheels,
			isPaintingInterior,
			isPaintingWorks,
			rimsColors,
			rimsColor,
			rimsColorsPrice,
			colorPickerSelectedColor,
			brakesColor,
			brakesColors,
			paintGroups,
			activePaintGroup,
			indexedColor,
			pearlColor,
			ownedVehiclePaint?.windowTint,
			loadVehiclePaint,
			nonExteriorCategories,
			activeIndex,
			ownedWheelSettings,
			wheelSettings,
			wheelsSetupPrice,
			activeVariants,
			wheelTireProfile,
			tireProfileChangePrice,
			selectedTireProfile,
		],
	);

	return { handleBuy };
};

export { useBuy };
